import { observer } from 'mobx-react-lite'
import { Button, Icon, Spinner, Menu, MenuItem, MenuDivider, Dialog, InputGroup, Classes, Intent } from '@blueprintjs/core'
import { ContextMenu2 } from '@blueprintjs/popover2'
import { IconNames } from '@blueprintjs/icons'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styles from './Sidebar.module.css'
import { useAppStore } from '../../stores/UnifiedStoreProvider'
import { View } from '../../db/types'
import { ToastService } from '../../services/ToastService'
import IssueTracking from '../tickets/IssueTracking'

interface SidebarProps {
  onNavigate: (view: View) => void
  currentView: View
}

/**
 * Sidebar navigation component
 * Includes project navigation and app sections
 * Uses MobX for state management with local persistence
 */
export const Sidebar = observer(({ onNavigate, currentView }: SidebarProps) => {
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; id: string; name: string; isFolder?: boolean }>({ open: false, id: '', name: '' })
  const [createFolderDialog, setCreateFolderDialog] = useState(false)
  const [createFolderType, setCreateFolderType] = useState<'private' | 'shared'>('private')
  const [folderName, setFolderName] = useState('')
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  
  const navigate = useNavigate()
  const { orgSlug, mindmapId } = useParams<{ orgSlug: string; mindmapId: string }>()
  const appStore = useAppStore()
  const { organization: organizationStore, layout: layoutStore, featureFlags: featureFlagsStore, preferences } = appStore
  
  // Data is now loaded by AppInitializer, no need to load here
  
  const handleMindmapClick = () => {
    // Just navigate to mindmap view - sidebar there handles the list
    onNavigate('mindmap')
  }
  
  const handleSelectMindmap = (id: string) => {
    if (orgSlug) {
      navigate(`/${orgSlug}/mindmap/${id}`)
    }
  }
  
  const handleCreateMindmap = async (folderId?: string, visibility: 'private' | 'shared' = 'private') => {
    if (orgSlug) {
      try {
        const newMindmap = await organizationStore.createMindmap(
          `New Mindmap ${new Date().toLocaleDateString()}`,
          'Created from sidebar',
          folderId,
          visibility
        )
        navigate(`/${orgSlug}/mindmap/${newMindmap.id}`)
      } catch (error) {
        console.error('Failed to create mindmap:', error)
        ToastService.show({ message: 'Failed to create mindmap', intent: Intent.DANGER })
      }
    }
  }
  
  const handleDeleteMindmap = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this mindmap?')) {
      try {
        await organizationStore.deleteMindmap(id)
        ToastService.show({ message: 'Mindmap deleted', intent: Intent.SUCCESS })
        // Navigate away if we're viewing the deleted mindmap
        if (mindmapId === id && orgSlug) {
          navigate(`/${orgSlug}/dashboard`)
        }
      } catch (error) {
        console.error('Failed to delete mindmap:', error)
        ToastService.show({ message: 'Failed to delete mindmap', intent: Intent.DANGER })
      }
    }
  }
  
  const handleRenameMindmap = async () => {
    if (renameDialog.name.trim()) {
      try {
        if (renameDialog.isFolder) {
          await organizationStore.renameFolder(renameDialog.id, renameDialog.name)
        } else {
          await organizationStore.renameMindmap(renameDialog.id, renameDialog.name)
        }
        ToastService.show({ message: 'Renamed successfully', intent: Intent.SUCCESS })
        setRenameDialog({ open: false, id: '', name: '' })
      } catch (error) {
        console.error('Failed to rename:', error)
        ToastService.show({ message: 'Failed to rename', intent: Intent.DANGER })
      }
    }
  }
  
  const handleCreateFolder = async () => {
    if (folderName.trim()) {
      try {
        await organizationStore.createFolder(folderName, createFolderType)
        ToastService.show({ message: 'Folder created', intent: Intent.SUCCESS })
        setCreateFolderDialog(false)
        setFolderName('')
      } catch (error) {
        console.error('Failed to create folder:', error)
        ToastService.show({ message: 'Failed to create folder', intent: Intent.DANGER })
      }
    }
  }
  
  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }
  
  const renderContextMenu = (mindmap: any, isFolder = false) => (
    <Menu>
      {!isFolder && (
        <>
          <MenuItem
            icon={IconNames.EDIT}
            text="Rename"
            onClick={() => setRenameDialog({ open: true, id: mindmap.id, name: mindmap.name, isFolder: false })}
          />
          <MenuItem
            icon={IconNames.DUPLICATE}
            text="Duplicate"
            onClick={() => organizationStore.duplicateMindmap(mindmap.id)}
          />
          <MenuDivider />
        </>
      )}
      {isFolder && (
        <>
          <MenuItem
            icon={IconNames.EDIT}
            text="Rename Folder"
            onClick={() => setRenameDialog({ open: true, id: mindmap.id, name: mindmap.name, isFolder: true })}
          />
          <MenuItem
            icon={IconNames.ADD}
            text="New Mindmap Here"
            onClick={() => handleCreateMindmap(mindmap.id)}
          />
          <MenuDivider />
        </>
      )}
      <MenuItem
        icon={IconNames.TRASH}
        text="Delete"
        intent={Intent.DANGER}
        onClick={() => isFolder ? organizationStore.deleteFolder(mindmap.id) : handleDeleteMindmap(mindmap.id)}
      />
    </Menu>
  )
  return (
    <div 
      className={`sidebar ${styles.sidebar} ${layoutStore.isSidebarCollapsed ? styles.collapsed : ''}`}
      style={{ 
        width: layoutStore.isSidebarCollapsed ? '60px' : `${layoutStore.sidebarWidth}px`,
        backgroundColor: 'var(--background-color)',
        borderRight: '1px solid var(--border-color)',
        color: 'var(--text-color)'
      }}
    >
      <div className={styles.navItems}>
        {featureFlagsStore.isWorkTabEnabled && (
          <Button
            className={`${styles.navButton} ${currentView === 'projects' ? styles.active : ''}`}
            onClick={() => onNavigate('projects')}
            icon={IconNames.PROJECTS}
            text={layoutStore.isSidebarCollapsed ? undefined : "Work"}
            fill
            minimal
            large
            alignText="left"
            title="Work / Projects"
          />
        )}
        <Button
          className={`${styles.navButton} ${currentView === 'mindmap' ? styles.active : ''}`}
          onClick={handleMindmapClick}
          icon={IconNames.MAP}
          text={layoutStore.isSidebarCollapsed ? undefined : "Mindmap"}
          fill
          minimal
          large
          alignText="left"
          title="Mindmap"
        />
        <Button
          className={`${styles.navButton} ${currentView === 'chat' ? styles.active : ''}`}
          onClick={() => onNavigate('chat')}
          icon={IconNames.CHAT}
          text={layoutStore.isSidebarCollapsed ? undefined : "Chat"}
          fill
          minimal
          large
          alignText="left"
          title="AI Chat"
        />
        <Button
          className={`${styles.navButton} ${currentView === 'changelog' ? styles.active : ''}`}
          onClick={() => onNavigate('changelog')}
          icon={IconNames.TIMELINE_EVENTS}
          text={layoutStore.isSidebarCollapsed ? undefined : "Changelog"}
          fill
          minimal
          large
          alignText="left"
          title="Release History"
        />
        {featureFlagsStore.isDashboardEnabled && (
          <Button
            className={`${styles.navButton} ${currentView === 'dashboard' ? styles.active : ''}`}
            onClick={() => onNavigate('dashboard')}
            icon={IconNames.DASHBOARD}
            text={layoutStore.isSidebarCollapsed ? undefined : "Dashboard"}
            fill
            minimal
            large
            alignText="left"
            title="Team Dashboard"
          />
        )}
        <Button
          className={`${styles.navButton} ${currentView === 'settings' ? styles.active : ''}`}
          onClick={() => onNavigate('settings')}
          icon={IconNames.COG}
          text={layoutStore.isSidebarCollapsed ? undefined : "Settings"}
          fill
          minimal
          large
          alignText="left"
          title="Settings"
        />
      </div>
      
      {/* Issue Tracking Section */}
      {!layoutStore.isSidebarCollapsed && (
        <IssueTracking />
      )}
      
      <div className={styles.collapseButton}>
        <Button
          icon={layoutStore.isSidebarCollapsed ? "chevron-right" : "chevron-left"}
          minimal
          small
          onClick={() => {
            layoutStore.toggleSidebar();
            preferences.toggleSidebarCollapsed();
          }}
          title={layoutStore.isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        />
      </div>
      
      {/* Rename Dialog */}
      <Dialog
        isOpen={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, id: '', name: '' })}
        title={renameDialog.isFolder ? "Rename Folder" : "Rename Mindmap"}
      >
        <div className={Classes.DIALOG_BODY}>
          <InputGroup
            value={renameDialog.name}
            onChange={(e) => setRenameDialog({ ...renameDialog, name: e.target.value })}
            placeholder="Enter new name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameMindmap()
            }}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setRenameDialog({ open: false, id: '', name: '' })}>Cancel</Button>
            <Button intent={Intent.PRIMARY} onClick={handleRenameMindmap}>Rename</Button>
          </div>
        </div>
      </Dialog>
      
      {/* Create Folder Dialog */}
      <Dialog
        isOpen={createFolderDialog}
        onClose={() => setCreateFolderDialog(false)}
        title="Create Folder"
      >
        <div className={Classes.DIALOG_BODY}>
          <div style={{ marginBottom: 8, fontSize: 12 }}>Folder Type</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Button small minimal intent={createFolderType === 'shared' ? Intent.PRIMARY : Intent.NONE} onClick={() => setCreateFolderType('shared')}>Shared</Button>
            <Button small minimal intent={createFolderType === 'private' ? Intent.PRIMARY : Intent.NONE} onClick={() => setCreateFolderType('private')}>Private</Button>
          </div>
          <InputGroup
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="Enter folder name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder()
            }}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setCreateFolderDialog(false)}>Cancel</Button>
            <Button intent={Intent.PRIMARY} onClick={handleCreateFolder}>Create</Button>
          </div>
        </div>
      </Dialog>
    </div>
  )
})

export default Sidebar 